/**
 * AuthZEN Smart Caching Layer
 * 
 * Implements intelligent authorization decision caching with:
 * - Cache invalidation on mutations
 * - Cross-session persistence (localStorage)
 * - Cache statistics for monitoring
 * - Manual cache control
 * 
 * Performance:
 * - 5-minute default TTL (configurable)
 * - In-memory primary cache (fast)
 * - localStorage fallback (persistent)
 * - Resource-level invalidation
 */

export interface CacheEntry {
	decision: boolean;
	timestamp: number;
	context?: any;
	obligations?: any[];
	requestId?: string;
}

export interface CacheStats {
	size: number;
	hits: number;
	misses: number;
	hitRate: number;
	oldestEntry: number | null;
}

export class AuthZENCache {
	private static cache = new Map<string, CacheEntry>();
	private static TTL_MS = 5 * 60 * 1000;  // 5 minutes default
	private static hits = 0;
	private static misses = 0;
	
	/**
	 * Generate cache key from request components
	 * 
	 * Cache key format: {subject_id}::{resource_type}::{resource_id}::{action}::{properties_hash}
	 * 
	 * Includes resource properties in hash since they affect authorization
	 */
	private static generateKey(
		subject: any,
		resource: any,
		action: any
	): string {
		const parts: string[] = [
			subject?.id || 'anonymous',
			resource?.type || 'unknown',
			resource?.id || '*',
			action?.name || 'unknown'
		];
		
		// Include resource properties in key if they affect authorization
		if (resource?.properties && Object.keys(resource.properties).length > 0) {
			// Create stable hash of properties
			const propsStr = JSON.stringify(resource.properties, Object.keys(resource.properties).sort());
			const propsHash = this.simpleHash(propsStr);
			parts.push(propsHash);
		}
		
		return parts.join('::');
	}
	
	/**
	 * Simple string hash for cache keys
	 */
	private static simpleHash(str: string): string {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			const char = str.charCodeAt(i);
			hash = (hash << 5) - hash + char;
			hash = hash & hash; // Convert to 32-bit integer
		}
		return Math.abs(hash).toString(36);
	}
	
	/**
	 * Get entry from cache
	 * 
	 * Returns null if:
	 * - Entry doesn't exist
	 * - Entry is expired
	 * 
	 * @returns {CacheEntry | null} Cached entry or null
	 */
	static get(subject: any, resource: any, action: any): CacheEntry | null {
		const key = this.generateKey(subject, resource, action);
		const entry = this.cache.get(key);
		
		if (!entry) {
			this.misses++;
			return null;
		}
		
		// Check if expired
		const age = Date.now() - entry.timestamp;
		if (age > this.TTL_MS) {
			this.cache.delete(key);
			this.misses++;
			return null;
		}
		
		this.hits++;
		return entry;
	}
	
	/**
	 * Store entry in cache
	 * 
	 * Stores in both memory and localStorage for cross-session persistence
	 */
	static set(
		subject: any,
		resource: any,
		action: any,
		decision: boolean,
		context?: any,
		obligations?: any[],
		requestId?: string
	): void {
		const key = this.generateKey(subject, resource, action);
		const entry: CacheEntry = {
			decision,
			timestamp: Date.now(),
			context,
			obligations,
			requestId
		};
		
		// Store in memory cache
		this.cache.set(key, entry);
		
		// Also persist to localStorage for cross-session caching
		try {
			localStorage.setItem(
				`authz:v2:${key}`,
				JSON.stringify(entry)
			);
		} catch (error) {
			// localStorage may be disabled or full, that's ok
			// We still have in-memory cache
		}
	}
	
	/**
	 * Invalidate cache for a specific resource
	 * 
	 * Call this after mutations to ensure fresh authorization checks
	 * 
	 * @param {string} resourceType - Resource type to invalidate
	 * @param {string} resourceId - Optional specific resource ID
	 * 
	 * @example
	 * ```typescript
	 * // After updating a user
	 * await updateUser(userId, data);
	 * AuthZENCache.invalidate('user', userId);  // ✅ Clear cache for this user
	 * 
	 * // After changing role assignments
	 * await updateRoles(userId);
	 * AuthZENCache.invalidate('user');  // ✅ Clear all user permissions
	 * ```
	 */
	static invalidate(resourceType: string, resourceId?: string): void {
		const pattern = resourceId
			? `::${resourceType}::${resourceId}::`
			: `::${resourceType}::`;
		
		let invalidatedCount = 0;
		
		// Remove matching entries from memory cache
		for (const [key, _] of this.cache.entries()) {
			if (key.includes(pattern)) {
				this.cache.delete(key);
				invalidatedCount++;
				
				// Also remove from localStorage
				try {
					localStorage.removeItem(`authz:v2:${key}`);
				} catch {}
			}
		}
		
		console.info(
			`[AuthZ] Invalidated ${invalidatedCount} cache entries for ` +
			`${resourceType}${resourceId ? ':' + resourceId : ' (all)'}`
		);
	}
	
	/**
	 * Clear all cached decisions
	 * 
	 * Useful after:
	 * - User role changes
	 * - Policy updates
	 * - Logout
	 * 
	 * @example
	 * ```typescript
	 * // After role change
	 * await assignRole(userId, 'admin');
	 * AuthZENCache.clearAll();  // ✅ Force re-check all permissions
	 * ```
	 */
	static clearAll(): void {
		const size = this.cache.size;
		
		this.cache.clear();
		this.hits = 0;
		this.misses = 0;
		
		// Clear localStorage cache too
		try {
			const keys = [];
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (key?.startsWith('authz:v2:')) {
					keys.push(key);
				}
			}
			keys.forEach(k => localStorage.removeItem(k));
		} catch {}
		
		console.info(`[AuthZ] Cleared all cached decisions (${size} entries)`);
	}
	
	/**
	 * Get cache statistics for monitoring
	 * 
	 * @returns {CacheStats} Cache performance statistics
	 * 
	 * @example
	 * ```typescript
	 * const stats = AuthZENCache.getStats();
	 * console.log(`Cache hit rate: ${stats.hitRate}%`);
	 * ```
	 */
	static getStats(): CacheStats {
		const timestamps = Array.from(this.cache.values()).map(e => e.timestamp);
		const totalRequests = this.hits + this.misses;
		
		return {
			size: this.cache.size,
			hits: this.hits,
			misses: this.misses,
			hitRate: totalRequests > 0 ? (this.hits / totalRequests) * 100 : 0,
			oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : null
		};
	}
	
	/**
	 * Set cache TTL (time-to-live) in milliseconds
	 * 
	 * Default: 5 minutes
	 * 
	 * @param {number} ttlMs - TTL in milliseconds
	 */
	static setTTL(ttlMs: number): void {
		if (ttlMs <= 0) {
			throw new Error('Cache TTL must be positive');
		}
		
		this.TTL_MS = ttlMs;
		console.info(`[AuthZ] Cache TTL set to ${ttlMs / 1000} seconds`);
	}
	
	/**
	 * Preload cache from localStorage
	 * 
	 * Call at app startup to restore cached decisions from previous session
	 */
	static async preload(): Promise<number> {
		let loaded = 0;
		
		try {
			const now = Date.now();
			
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				
				if (key?.startsWith('authz:v2:')) {
					try {
						const data = JSON.parse(localStorage.getItem(key) || '{}');
						
						// Only restore if not expired
						if (data.timestamp && (now - data.timestamp) < this.TTL_MS) {
							const cacheKey = key.substring('authz:v2:'.length);
							this.cache.set(cacheKey, data as CacheEntry);
							loaded++;
						} else {
							// Remove expired entry
							localStorage.removeItem(key);
						}
					} catch {
						// Invalid entry, remove it
						localStorage.removeItem(key);
					}
				}
			}
			
			if (loaded > 0) {
				console.info(`[AuthZ] Preloaded ${loaded} cached decisions from localStorage`);
			}
			
		} catch (error) {
			console.warn('[AuthZ] Failed to preload cache from localStorage:', error);
		}
		
		return loaded;
	}
}

/**
 * React hook for manual cache control
 * 
 * Provides functions to invalidate cache after mutations
 * 
 * @example
 * ```typescript
 * function UserEditForm({ userId }) {
 *   const { invalidate, clearAll } = useAuthzCacheControl();
 *   
 *   const handleSave = async () => {
 *     await saveUser(userId, data);
 *     invalidate('user', userId);  // ✅ Clear cache for this user
 *     toast.success('User updated');
 *   };
 *   
 *   return <form onSubmit={handleSave}>...</form>;
 * }
 * ```
 */
export function useAuthzCacheControl() {
	return {
		invalidate: AuthZENCache.invalidate.bind(AuthZENCache),
		clearAll: AuthZENCache.clearAll.bind(AuthZENCache),
		getStats: AuthZENCache.getStats.bind(AuthZENCache)
	};
}

